import cx from "classnames";
import { pick } from "lodash";
import { type ForwardedRef, useEffect, useImperativeHandle, useState } from "react";
import { ExcelFields } from "./ExcelFields";
import { ExcelGeneral } from "./ExcelGeneral";
import type { DisabledData, ExportHandleRef } from "../ExportModal";
import { Exporter } from "../../Exporter";
import { type FormController, useFormController } from "@/hooks/useFormController";
import {
  getDefaultExportName,
  getAvailableExcelScopeValues,
  hasInvalidFilenameChars,
  readStoredExcelExportSettings,
  storeExportSettings,
  isAccessibilityReportAvailable,
  type CategoryFields
} from "../ExportModal.utils";
import { usePermissionCheck } from "@/hooks/usePermissionCheck";
import { GetDoc } from "@/Utils/StateLib";
import { Tabs } from "@theorgchartdev/storybook";
import { useDirectorySearchContext } from "@/providers/DirectorySearchProvider";
import { useOptionalDirectoryTabularContext } from "@/providers/DirectoryTabularProvider";
import { useOptionalPlanningTabularContext } from "@/providers/PlanningTabularProvider";
import { useReportingContext } from "@/providers/ReportsProvider";
import { useOptionalTabularDisplayContext } from "@/providers/TabularDisplayProvider";
import { getFieldInfoFromTempColField } from "@/Utils/Table/PosMgmtUtils";
import { isInDirectory } from "@coreapp/utils/isInDirectory";
import { isInReporting } from "@coreapp/utils/isInReporting";
import { toServerFilterValue } from "@coreapp/utils/FormatFieldValue";
import { appstr } from "@coreapp/utils/appstr";
import { QRulePair } from "@coreapp/CoreApp/QRulePair";
import { enumObjClassNames } from "@coreapp/constants";
import { getEmployeePositionFields } from "../ExportModal.utils";
import { QObjDBFieldDef } from "@coreapp/CoreApp/QObjDBFieldDef";

type Props = {
  handleRef: ForwardedRef<ExportHandleRef>;
  setDisabled: (key: string, value: DisabledData) => void;
  className?: string;
};

export type FieldData = {
  name: string;
  scope: string;
  chainOfCommand: boolean;
  systemCalculations: boolean;
  accessibilityReport: boolean;
  includeFunctionalStatements: boolean;
  categoryFields: CategoryFields;
};

export type TabProps = {
  formController: FormController<FieldData>;
  categoryKey: "employee" | "position";
  fieldFilter?: (fieldName: string) => boolean;
};

export const getTabularColumnFieldNames = (columns: QObjDBFieldDef[] = []) => {
  return Array.from(
    new Set(
      columns
        .map(fieldDef => fieldDef.GetFieldName())
        .filter((fieldName): fieldName is string => Boolean(fieldName))
    )
  );
};

export const resolveInitialExcelFields = (
  inDirectory: boolean,
  inReporting: boolean,
  isPlanningTableExport: boolean,
  isPositionChart: boolean,
  directoryColumnNames: string[],
  reportColumnNames: string[],
  directoryTabularColumns?: QObjDBFieldDef[],
  planningTabularColumns?: QObjDBFieldDef[]
) => {
  if (inDirectory) {
    if (isPositionChart) {
      const visibleDirectoryFields = getTabularColumnFieldNames(directoryTabularColumns);
      if (visibleDirectoryFields.length > 0) {
        return visibleDirectoryFields;
      }
    }

    return directoryColumnNames;
  }

  if (inReporting) {
    return reportColumnNames;
  }

  if (isPlanningTableExport) {
    return getTabularColumnFieldNames(planningTabularColumns);
  }

  return undefined;
};

export const normalizeExcelFieldSelection = (
  isPositionChart: boolean,
  isTableExport: boolean,
  fields: string[],
  availableFields: string[],
  excludedFields?: string[]
) => {
  if (!isPositionChart) {
    return {
      fields,
      excludedFields
    };
  }

  const availableFieldSet = new Set(availableFields);
  const deduplicatedAvailableFields = Array.from(availableFieldSet);
  const includedFields = Array.from(new Set(fields)).filter(field => availableFieldSet.has(field));
  const includedFieldSet = new Set(includedFields);
  const filteredExcludedFields = Array.from(new Set(excludedFields)).filter(
    field => availableFieldSet.has(field) && !includedFieldSet.has(field)
  );

  if (includedFields.length === 0 && filteredExcludedFields.length === 0) {
    return {
      fields: deduplicatedAvailableFields,
      excludedFields: []
    };
  }

  const selectedFieldSet = new Set([...includedFields, ...filteredExcludedFields]);
  const newFields = deduplicatedAvailableFields.filter(field => !selectedFieldSet.has(field));

  return {
    fields: isTableExport ? includedFields : [...includedFields, ...newFields],
    excludedFields: isTableExport
      ? [...filteredExcludedFields, ...newFields]
      : filteredExcludedFields
  };
};

export const ExcelContent = (props: Props) => {
  const doc = GetDoc();
  const isPositionChart = doc?.IsPositionDocument();
  const inDirectory = isInDirectory();
  const inReporting = isInReporting();
  const tabularDisplayContext = useOptionalTabularDisplayContext();
  const isTabularDisplay = tabularDisplayContext?.isTabularDisplay ?? false;

  const directoryContext = useDirectorySearchContext();
  const directoryTabularContext = useOptionalDirectoryTabularContext();
  const useServerExport =
    (inDirectory && (doc?.IsServerChart() || !!directoryTabularContext)) || inReporting;
  const planningTabularContext = useOptionalPlanningTabularContext();
  const reportingContext = useReportingContext();
  const { currentOpenReport } = reportingContext;
  const isPlanningTableExport =
    doc?.IsPlanChart?.() === true &&
    doc?.IsPositionDocument?.() === true &&
    isTabularDisplay &&
    !!planningTabularContext;
  const isTableExport = inDirectory || inReporting || isPlanningTableExport;
  const { hasActiveBLS, showFunctionalStatement } = usePermissionCheck();

  const [currentTab, setCurrentTab] = useState(0);

  const formController = useFormController<FieldData>(
    () => {
      const storedSettings = readStoredExcelExportSettings(currentOpenReport);

      const defaultCategoryFields: CategoryFields = {
        position: { included: [], excluded: [] },
        employee: { included: [], excluded: [] }
      };

      let categoryFields = storedSettings.categoryFields ?? defaultCategoryFields;

      const storedIsEmpty =
        categoryFields.employee.included.length === 0 &&
        categoryFields.employee.excluded.length === 0 &&
        categoryFields.position.included.length === 0 &&
        categoryFields.position.excluded.length === 0;

      if (storedIsEmpty) {
        const initialFields = resolveInitialExcelFields(
          inDirectory,
          inReporting,
          isPlanningTableExport,
          !!isPositionChart,
          directoryContext.dirSearchState.columnNames,
          reportingContext.reportSearchState.columnNames,
          directoryTabularContext?.state.columns,
          planningTabularContext?.state.columns
        );

        const { keys, employeeKeys } = doc
          ? getEmployeePositionFields(doc)
          : { keys: [], employeeKeys: [] };

        const allowedFieldSet = new Set(storedSettings.allowedExcelFields);

        const effectiveInitialFields =
          initialFields && initialFields.length > 0
            ? initialFields.filter((f: string) => allowedFieldSet.has(f))
            : isPositionChart
              ? Array.from(new Set(keys.concat(employeeKeys))).filter((f: string) =>
                  allowedFieldSet.has(f)
                )
              : [...storedSettings.allowedExcelFields];

        if (isPositionChart) {
          const keySet = new Set(keys);
          const employeeKeySet = new Set(employeeKeys);
          categoryFields = {
            position: {
              included: effectiveInitialFields.filter((f: string) => keySet.has(f)),
              excluded: []
            },
            employee: {
              included: effectiveInitialFields.filter((f: string) => employeeKeySet.has(f)),
              excluded: []
            }
          };
        } else {
          categoryFields = {
            position: { included: [], excluded: [] },
            employee: { included: effectiveInitialFields, excluded: [] }
          };
        }
      }

      const availableScopeValues = getAvailableExcelScopeValues(doc, hasActiveBLS);
      const storedScope = storedSettings.scope ?? "currentbranch";
      const validScope = availableScopeValues.includes(storedScope) ? storedScope : "currentbranch";

      const initialState: FieldData = {
        name: getDefaultExportName(currentOpenReport),
        scope: isTableExport ? "filter" : validScope,
        chainOfCommand: storedSettings.chainOfCommand,
        systemCalculations: storedSettings.systemCalculations,
        includeFunctionalStatements:
          !!showFunctionalStatement &&
          !isTableExport &&
          (storedSettings.includeFunctionalStatements ?? false),
        accessibilityReport:
          !isTableExport &&
          isAccessibilityReportAvailable(doc) &&
          storedSettings.accessibilityReport,
        categoryFields
      };

      return initialState;
    },
    {
      onChange: (nextFieldData: FieldData, updatedKey: keyof FieldData) => {
        storeExportSettings("excel", updatedKey, nextFieldData[updatedKey], currentOpenReport);
        props.setDisabled("hasFields", { isDisabled: !hasValidFieldSelection(nextFieldData) });
        if (updatedKey === "name") {
          const isInvalid = hasInvalidFilenameChars(nextFieldData.name);
          props.setDisabled("invalidFilename", {
            isDisabled: isInvalid,
            tooltip: isInvalid ? appstr("exportFilenameInvalidChars") : undefined
          });
        }
      }
    }
  );

  useEffect(() => {
    const isExportDisabled = !hasValidFieldSelection(formController.fieldData);
    props.setDisabled("hasFields", { isDisabled: isExportDisabled });
    const isInvalid = hasInvalidFilenameChars(formController.fieldData.name);
    props.setDisabled("invalidFilename", {
      isDisabled: isInvalid,
      tooltip: isInvalid ? appstr("exportFilenameInvalidChars") : undefined
    });
  }, []);

  useImperativeHandle(props.handleRef, () => ({
    handleExport
  }));

  const handleExport = async () => {
    const doc = GetDoc();
    let filterObj = {};
    let results = [];

    const { categoryFields } = formController.fieldData;
    const positionExcluded = new Set(categoryFields.position.excluded);
    const employeeExcluded = new Set(categoryFields.employee.excluded);
    const filteredFields = Array.from(
      new Set([
        ...categoryFields.position.included.filter(f => !positionExcluded.has(f)),
        ...categoryFields.employee.included.filter(f => !employeeExcluded.has(f))
      ])
    );

    if (useServerExport || inReporting || isPlanningTableExport) {
      if (
        (inDirectory && doc?.IsPositionDocument() && directoryTabularContext) ||
        isPlanningTableExport
      ) {
        const tableContext =
          isPlanningTableExport && planningTabularContext
            ? planningTabularContext
            : directoryTabularContext;
        const { state, searchCtrlRef } = tableContext!;

        let sort = {};
        const sortConfig = state.sorting[0];
        if (sortConfig) {
          const { fieldName, fieldClassName } = getFieldInfoFromTempColField(sortConfig.id);
          if (fieldName) {
            sort = {
              field: fieldName,
              direction: sortConfig.desc ? -1 : 1,
              forHolder: fieldClassName === enumObjClassNames.box
            };
          }
        }

        filterObj = {
          filterState: state.columnFilters
            .map((filter: any) => {
              const { fieldName, fieldClassName } = getFieldInfoFromTempColField(filter.id);
              if (!fieldName) {
                return null;
              }

              return {
                field: fieldName,
                value: ((filter.value as any[]) ?? []).map(toServerFilterValue),
                forHolder: fieldClassName === enumObjClassNames.box
              };
            })
            .filter(Boolean),
          sort,
          queryString: searchCtrlRef.current?.getQueryString() ?? ""
        };
      } else {
        const { searchCtrl, tableHandleRef } = inReporting ? reportingContext : directoryContext;
        const controllerState = tableHandleRef.current?.getControllerData();

        const filters = Object.entries(controllerState?.filter?.filterConfig ?? {}).map(
          ([fieldName, filterConfig]) => {
            const rule = new QRulePair();

            rule.InitRulePair(
              fieldName,
              fieldName,
              null,
              "inlist",
              (filterConfig as any).values ?? []
            );
            return rule.Marshall();
          }
        );

        let sort = {};
        const sortConfig = controllerState?.sort?.sortConfig;
        if (sortConfig) {
          sort = {
            field: sortConfig.column?.field,
            direction: sortConfig.order === "asc" ? 1 : -1
          };
        }

        filterObj = {
          filters,
          sort,
          queryString: searchCtrl.current?.getQueryString() ?? ""
        };
      }
    } else if (isTableExport) {
      const { tableHandleRef } = inReporting ? reportingContext : directoryContext;

      results = (tableHandleRef.current?.getProcessedData() ?? []).map((result: unknown) => {
        return pick(result, filteredFields);
      });
    }

    const exporter = new Exporter();
    await exporter.export({
      type: "excel",
      fieldData: formController.fieldData,
      isTableExport,
      useServerExport,
      filterObj,
      results
    });

    return true;
  };

  let exportTabs: { header: string; dataTestId: string; content: React.ReactNode }[] = [
    {
      header: appstr("General"),
      dataTestId: "export-modal-tab-general",
      content: <ExcelGeneral formController={formController} isTableExport={isTableExport} />
    }
  ];

  const { keys, employeeKeys } = doc
    ? getEmployeePositionFields(doc)
    : { keys: [], employeeKeys: [] };

  if (isPositionChart && employeeKeys && keys) {
    exportTabs.push(
      {
        header: appstr("Employee fields"),
        dataTestId: "export-modal-tab-employee-fields",
        content: (
          <ExcelFields
            key="employee-fields"
            formController={formController}
            categoryKey="employee"
            fieldFilter={field => employeeKeys.includes(field)}
          />
        )
      },
      {
        header: appstr("Position fields"),
        dataTestId: "export-modal-tab-position-fields",
        content: (
          <ExcelFields
            key="position-fields"
            formController={formController}
            categoryKey="position"
            fieldFilter={field => keys.includes(field)}
          />
        )
      }
    );
  } else {
    exportTabs.push({
      header: appstr("Fields"),
      dataTestId: "export-modal-tab-fields",
      // Non-position charts have a single field list; it writes to the "employee" bucket by convention.
      content: (
        <ExcelFields key="all-fields" formController={formController} categoryKey="employee" />
      )
    });
  }

  return (
    <Tabs
      customClass={cx("ocn-chart-explorer-tabs", "oc-overflow-hidden")}
      customContentClass={cx("oc-overflow-auto", "ocn-list-scrollbar", "oc-fill-height")}
      tabIndex={currentTab}
      onTabChange={setCurrentTab}
      tabs={exportTabs}
    />
  );
};

const hasValidFieldSelection = (fieldData: FieldData) => {
  const totalIncluded =
    fieldData.categoryFields.employee.included.length +
    fieldData.categoryFields.position.included.length;
  return fieldData.systemCalculations || totalIncluded > 0;
};
